// src/pages/Users/Techniques/TechniquesView.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import Button from "../../../components/Button/Button";

import { getAllTechniques } from "../../../services/techniquesServices";

import {
  getUserFavorites,
  addFavorite,
  removeFavorite,
} from "../../../services/usersTechniquesServices";

import TechniquesToolbar from "./TechniquesToolbar";
import TechniqueCard from "./TechniqueCard";
import TechniqueModal from "./TechniqueModal";
import SkeletonCard from "./SkeletonCard";
import EmptyState from "./EmptyState";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

// Options par défaut
// ⬇️ remplace la ligne sort par défaut
const DEFAULT_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  family: "Toutes les familles",
  uv2: "Toutes les fiches",
  showFav: false,
  sort: "beltFamily", // ⬅️ NEW: Ceinture > Famille > Nom
  perPage: 24,
};

// ---------- Helpers tri ceintures / familles ----------
const BELT_BASE = [
  "blanche", // si jamais tu en as
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

const FAMILY_ORDER = [
  "Jambe",
  "Hanche",
  "Epaule",
  "Bras",
  "Sutemi Waza",
  "Immobilisation",
  "Shime Waza",
  "Kansetsu-Waza",
  "Self Défense",
  "Kata",
  "Vocabulaire",
  "Les Bases",
  "Ukemi",
];

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

const strip = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

// Renvoie un rang numérique croissant: jaune < ... < noire < 1er dan < 2e dan < ...
function beltRank(ceinture = "") {
  const c = strip(ceinture);

  // Détection DAN : "1er dan", "2e dan", "3e dan"...
  const m = c.match(/(\d+)\s*(?:er|e)?\s*dan/);
  if (m) {
    const n = parseInt(m[1], 10) || 1;
    // base noire = 6, 1er dan = 7, 2e dan = 8, ...
    return 6 + n;
  }

  // Transitions possibles "jaune-orange", "orange / verte", etc.
  // On positionne au milieu entre les deux couleurs si on les trouve.
  const colorsFound = BELT_BASE.map((color, idx) =>
    c.includes(color) ? idx : -1
  ).filter((i) => i >= 0);

  if (colorsFound.length === 1) return colorsFound[0]; // couleur simple
  if (colorsFound.length >= 2) {
    const avg = colorsFound.reduce((a, b) => a + b, 0) / colorsFound.length;
    return avg; // ex: "jaune-orange" ~ 0.5 si blanche est 0, sinon ajuste si tu n'utilises pas blanche
  }

  // Couleurs simples (fallback exact)
  const idx = BELT_BASE.indexOf(c);
  if (idx >= 0) return idx;

  // Par défaut : très grand => à la fin
  return Number.POSITIVE_INFINITY;
}

function familyRank(famille = "") {
  const f = FAMILY_ORDER.findIndex((x) => strip(x) === strip(famille));
  return f >= 0 ? f : 999; // inconnu en fin de liste
}

// Helpers
function uniqSorted(arr) {
  return Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
    String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
  );
}
function normalizeUV2(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function TechniquesView() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // --- État ---
  const [allTechniques, setAllTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  const [favorites, setFavorites] = useState([]); // [technique_id]
  const userId = useMemo(() => {
    try {
      const info = JSON.parse(localStorage.getItem("userInfo"));
      return info?.userId ?? null;
    } catch {
      return null;
    }
  }, []);

  // UI state (persisté localStorage)
  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("techniques_ui_state");
    return saved ? { ...DEFAULT_STATE, ...JSON.parse(saved) } : DEFAULT_STATE;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search);
  const [showCopied, setShowCopied] = useState(false);

  // Modale
  const [opened, setOpened] = useState(null); // technique ouverte

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem("techniques_ui_state", JSON.stringify(next));
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(
      () => persistUi({ ...ui, search: debouncedSearch }),
      300
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [debouncedSearch]);

  // Fetch
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getAllTechniques();
        setAllTechniques(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!userId) return;
      const favs = await getUserFavorites(userId);
      setFavorites(favs.map((f) => f.technique_id));
    })();
  }, [userId]);

  // Options dynamiques (alimentées par les données)
  const dynamicOptions = useMemo(() => {
    const uniqueGrades = Array.from(
      new Set(allTechniques.map((t) => t.ceinture).filter(Boolean))
    );

    // Tri des ceintures dans l'ordre défini + DAN à la suite
    uniqueGrades.sort((a, b) => beltRank(a) - beltRank(b));

    const families = Array.from(
      new Set(allTechniques.map((t) => t.famille).filter(Boolean))
    ).sort((a, b) => familyRank(a) - familyRank(b) || collatorFR.compare(a, b));

    const uv2 = Array.from(
      new Set(
        allTechniques.flatMap((t) =>
          (t.uv2
            ? Array.isArray(t.uv2)
              ? t.uv2
              : String(t.uv2)
                  .split(/[,\s]+/)
                  .map((x) => x.trim())
            : []
          ).filter(Boolean)
        )
      )
    ).sort((a, b) => collatorFR.compare(a, b));

    return {
      grades: ["Toutes les ceintures", ...uniqueGrades],
      families: ["Toutes les familles", ...families],
      uv2: ["Toutes les fiches", ...uv2],
    };
  }, [allTechniques]);

  // Filtrage + tri
  const filtered = useMemo(() => {
    const term = ui.search.trim().toLowerCase();

    let arr = allTechniques.filter((t) => {
      const matchSearch =
        !term ||
        t.nom?.toLowerCase().includes(term) ||
        t.traduction?.toLowerCase().includes(term) ||
        t.famille?.toLowerCase().includes(term);

      const matchGrade =
        ui.grade === "Toutes les ceintures" || t.ceinture === ui.grade;

      const matchFamily =
        ui.family === "Toutes les familles" || t.famille === ui.family;

      const matchUV2 =
        ui.uv2 === "Toutes les fiches" || normalizeUV2(t.uv2).includes(ui.uv2);

      const matchFav = !ui.showFav || favorites.includes(t.id);

      return matchSearch && matchGrade && matchFamily && matchUV2 && matchFav;
    });

    // ... dans le useMemo(filtered)
    arr.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a.nom), String(b.nom));
      }
      if (ui.sort === "grade") {
        const rA = beltRank(a.ceinture);
        const rB = beltRank(b.ceinture);
        return rA - rB || collatorFR.compare(String(a.nom), String(b.nom));
      }
      if (ui.sort === "family") {
        const fA = familyRank(a.famille);
        const fB = familyRank(b.famille);
        return fA - fB || collatorFR.compare(String(a.nom), String(b.nom));
      }

      // ⬇️ Défaut: Ceinture > Famille > Nom
      const rA = beltRank(a.ceinture);
      const rB = beltRank(b.ceinture);
      if (rA !== rB) return rA - rB;

      const fA = familyRank(a.famille);
      const fB = familyRank(b.famille);
      if (fA !== fB) return fA - fB;

      return collatorFR.compare(String(a.nom), String(b.nom));
    });

    return arr;
  }, [allTechniques, ui, favorites]);

  const visible = useMemo(
    () => filtered.slice(0, ui.perPage),
    [filtered, ui.perPage]
  );

  // Actions favoris (optimistes)
  const toggleFavorite = async (techId) => {
    if (!userId) return;
    const isFav = favorites.includes(techId);
    // Optimiste
    setFavorites((prev) =>
      isFav ? prev.filter((id) => id !== techId) : [...prev, techId]
    );
    try {
      if (isFav) await removeFavorite(userId, techId);
      else await addFavorite(userId, techId);
    } catch {
      // rollback simpliste
      setFavorites((prev) =>
        isFav ? [...prev, techId] : prev.filter((id) => id !== techId)
      );
    }
  };

  // Partage des filtres via URL (query-string)
  const copyShareLink = async () => {
    try {
      const params = new URLSearchParams();
      if (ui.search) params.set("q", ui.search);
      if (ui.grade !== DEFAULT_STATE.grade) params.set("grade", ui.grade);
      if (ui.family !== DEFAULT_STATE.family) params.set("famille", ui.family);
      if (ui.uv2 !== DEFAULT_STATE.uv2) params.set("uv2", ui.uv2);
      if (ui.showFav) params.set("fav", "1");
      if (ui.sort !== DEFAULT_STATE.sort) params.set("sort", ui.sort);

      const url = `${window.location.origin}${
        window.location.pathname
      }?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    } catch {}
  };

  // Reset
  const resetFilters = () => persistUi(DEFAULT_STATE);

  // Charger plus
  const loadMore = () => persistUi({ ...ui, perPage: ui.perPage + 24 });

  return (
    <section className="w-full">
      <ScrollIndicator />
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Techniques Karaté"
          align="center"
          className="mb-1"
        />

        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
          {/* Navigation */}
          <div className="mb-6">
            <DashboardNavigation role={userRole} />
          </div>

          {/* Carte globale */}
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
            {/* Header gradient */}
            <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold">
                    Bibliothèque de techniques de Karaté
                  </h2>
                  <p className="text-white/90 text-sm">
                    A l'aide des filtres, retrouvez rapidement la technique recherchée.
                    <br />
                    Vous pouvez également rajouter des techniques à vos favoris pour les retrouver plus facilement.
                  </p>
                </div>
                <div className="text-sm">
                  <span className="font-semibold">{filtered.length}</span>{" "}
                  résultat(s)
                  {ui.showFav && (
                    <span className="ml-2 opacity-90">
                      — Favoris:{" "}
                      <span className="font-semibold">{favorites.length}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
              <TechniquesToolbar
                dynamicOptions={dynamicOptions}
                ui={ui}
                onChange={(patch) => persistUi({ ...ui, ...patch })}
                debouncedSearch={debouncedSearch}
                setDebouncedSearch={setDebouncedSearch}
                onReset={resetFilters}
                onShare={copyShareLink}
                shareCopied={showCopied}
              />
            </div>

            {/* Contenu */}
            <div className="p-4 sm:p-6">
              {/* Loading */}
              {loading && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              )}

              {/* Empty */}
              {!loading && filtered.length === 0 && <EmptyState />}

              {/* Liste */}
              {!loading && filtered.length > 0 && (
                <>
                  {/* Vue grille unique */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {visible.map((t) => (
                      <TechniqueCard
                        key={t.id}
                        technique={t}
                        isFavorite={favorites.includes(t.id)}
                        onToggleFavorite={() => toggleFavorite(t.id)}
                        onOpen={() => setOpened(t)}
                      />
                    ))}
                  </div>

                  {visible.length < filtered.length && (
                    <div className="text-center mt-6">
                      <Button
                        text="Charger plus"
                        onClick={loadMore}
                        className="rounded-xl"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modale */}
      {opened && (
        <TechniqueModal technique={opened} onClose={() => setOpened(null)} />
      )}
    </section>
  );
}
